import type { FunctionComponent } from "react";
import React from "react";
import { WizardPath } from "../WizardPath/WizardPath";
import type {
  OrderStatus,
  OrderStatusLabel,
  QuoteStatus,
  SampleRequestStatus,
  SampleStatusLabel,
} from "../../types/types";

const orderStatusLabels: OrderStatusLabel[] = [
  "Placed",
  "Accepted",
  "In Progress",
  "Invoiced/Payment Received",
  "Shipped",
  "Completed",
];

const sampleStatusLabels: SampleStatusLabel[] = [
  "New",
  "Accepted",
  "In Progress",
  "Shipped",
  "Completed",
];

const pricedSampleStatusLabels: SampleStatusLabel[] = [
  "New",
  "Accepted",
  "In Progress",
  "Invoiced/Payment Received",
  "Shipped",
  "Completed",
];

const pricedBuyerSubmittedSampleStatusLabels: SampleStatusLabel[] = [
  "New",
  "Price Received",
  "Accepted",
  "In Progress",
  "Invoiced/Payment Received",
  "Shipped",
  "Completed",
];

const pricedBuyerSubmittedSampleStatusLabelsSeller: SampleStatusLabel[] = [
  "New",
  "Responded",
  "Accepted",
  "In Progress",
  "Invoiced/Payment Received",
  "Shipped",
  "Completed",
];

export const isPartManualSampleStatusChanges = (
  status: SampleRequestStatus,
  isPriced: boolean
): boolean => {
  const manualAcceptedSampleStatusChanges: SampleRequestStatus[] = [
    "accepted",
    "in_progress",
    "shipped",
    "completed",
  ];

  if (isPriced && status === "invoiced") {
    return true;
  }

  return manualAcceptedSampleStatusChanges.includes(status);
};

const sampleMap = new Map([
  ["new", 0],
  ["requested", 0],
  ["accepted", 1],
  ["in_progress", 2],
  ["shipped", 3],
  ["completed", 4],
]);

const pricedSellerSampleMap = new Map([
  ["new", 0],
  ["requested", 0],
  ["responded", 1],
  ["accepted", 2],
  ["in_progress", 3],
  ["invoiced", 4],
  ["payment_received", 4],
  ["shipped", 5],
  ["completed", 6],
]);

const pricedBuyerSubmittedSampleMap = new Map([
  ["new", 0],
  ["requested", 0],
  ["responded", 1],
  ["price_received", 1],
  ["accepted", 2],
  ["in_progress", 3],
  ["invoiced", 4],
  ["payment_received", 4],
  ["shipped", 5],
  ["completed", 6],
]);

export const getOrderStatusItemsForDropDown = (status: OrderStatus) => {
  if (status === "payment_received") {
    return ["Completed"];
  } else return orderStatusLabels.filter((label) => label !== "Placed");
};

const quoteStatusLabels = ["Placed", "Requested", "Responded", "Accepted"];

const orderMap = new Map([
  ["new", 0],
  ["accepted", 1],
  ["in_progress", 2],
  ["invoiced", 3],
  ["payment_received", 3],
  ["shipped", 4],
  ["completed", 5],
]);

const quoteMap = new Map([
  ["new", 0],
  ["requested", 1],
  ["responded", 2],
  ["accepted", 3],
]);
interface TransactionStatusPathProps {
  transactionStatus: OrderStatus | QuoteStatus | SampleRequestStatus;
  view:
    | "sellerOrder"
    | "sellerQuote"
    | "buyerOrder"
    | "buyerQuote"
    | "sellerSample"
    | "buyerSample";
  isPricedSample?: boolean;
  isBuyerSubmittedPricedSample?: boolean;
}

const getTransactionStatusPathPoint = ({
  transactionStatus,
  view,
  isPricedSample,
  isBuyerSubmittedPricedSample,
}: TransactionStatusPathProps): number | undefined => {
  switch (view) {
    case "sellerOrder":
      return orderMap.get(transactionStatus);
    case "sellerQuote":
      return quoteMap.get(transactionStatus);
    case "buyerOrder":
      return orderMap.get(transactionStatus);
    case "buyerQuote":
      return quoteMap.get(transactionStatus);
    case "sellerSample":
    case "buyerSample":
      if (isBuyerSubmittedPricedSample) {
        return pricedBuyerSubmittedSampleMap.get(transactionStatus);
      }
      if (isPricedSample) {
        return pricedSellerSampleMap.get(transactionStatus);
      }
      return sampleMap.get(transactionStatus);
    default:
      return 0;
  }
};

const getViewStatusLabel = (
  view: string,
  isPricedSample?: boolean,
  isBuyerSubmittedPricedSample?: boolean
): SampleStatusLabel[] | OrderStatusLabel[] | string[] => {
  switch (view) {
    case "sellerQuote":
      return quoteStatusLabels;
    case "buyerQuote":
      return quoteStatusLabels;
    case "sellerSample":
      if (isBuyerSubmittedPricedSample) {
        return pricedBuyerSubmittedSampleStatusLabelsSeller;
      }
      return isPricedSample ? pricedSampleStatusLabels : sampleStatusLabels;
    case "buyerSample":
      if (isBuyerSubmittedPricedSample) {
        return pricedBuyerSubmittedSampleStatusLabels;
      }
      return isPricedSample ? pricedSampleStatusLabels : sampleStatusLabels;
    default:
      return orderStatusLabels;
  }
};

export const TransactionStatusPath: FunctionComponent<TransactionStatusPathProps> =
  ({
    transactionStatus,
    view,
    isPricedSample,
    isBuyerSubmittedPricedSample,
  }) => {
    const currentPoint =
      getTransactionStatusPathPoint({
        transactionStatus,
        view,
        isPricedSample,
        isBuyerSubmittedPricedSample,
      }) || 0;

    const labels = getViewStatusLabel(
      view,
      isPricedSample,
      isBuyerSubmittedPricedSample
    );

    return <WizardPath labels={labels} currentPoint={currentPoint} />;
  };
